/**
 * Plant Mapping API Service
 * Handles all plant mapping related API calls
 */

import apiClient from './api';

// Types
export interface PlantLayout {
  id: number;
  plant: number;
  name: string;
  background_image: string;
  width: number;
  height: number;
  default_zoom: number;
  default_center_x: number;
  default_center_y: number;
  background_opacity: number;
  workshop_count: number;
  positioned_count: number;
  created_at: string;
  updated_at: string;
}

export interface WorkshopPosition {
  id: number;
  workshop: WorkshopBasic;
  x: number;
  y: number;
  absolute_x: number;
  absolute_y: number;
  size: number;
  color: string;
  is_positioned: boolean;
  zone: string;
  layer: number;
  last_moved_at: string;
  last_moved_by_name: string;
  created_at: string;
  updated_at: string;
}

export interface WorkshopBasic {
  id: number;
  workshop_number: number;
  title: string;
  status: string;
  cc_category: string;
  sqdcm_category: string;
  category: string;
  plant: number;
  department: number;
  department_name: string;
  estimated_savings_per_year: number;
  estimated_savings_unit: string;
  startup_date: string | null;
  deadline: string | null;
  alv_id: string | null;
}

export interface PlantZone {
  id: number;
  name: string;
  description: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  color: string;
  opacity: number;
  border_color: string;
  border_style: string;
  department: number | null;
  capacity: number | null;
  is_active: boolean;
  workshop_count: number;
  is_over_capacity: boolean;
}

export interface PositionHistory {
  id: number;
  workshop_number: number;
  workshop_title: string;
  old_x: number | null;
  old_y: number | null;
  old_is_positioned: boolean;
  new_x: number;
  new_y: number;
  new_is_positioned: boolean;
  changed_by_name: string;
  changed_at: string;
  change_reason: string;
  notes: string;
}

export interface PlantMappingDashboard {
  layout: PlantLayout;
  positions: WorkshopPosition[];
  zones: PlantZone[];
  unpositioned_workshops: WorkshopBasic[];
  statistics: {
    total_workshops: number;
    positioned_workshops: number;
    unpositioned_workshops: number;
    total_zones: number;
    workshops_by_status: Array<{ status: string; count: number }>;
    workshops_by_department: Array<{ department__name: string; count: number }>;
  };
}

export interface DragPosition {
  id: number;
  x: number;
  y: number;
  is_positioned?: boolean;
  zone?: string;
}

// Plant Layout API
export const plantLayoutApi = {
  // List plant layouts
  list: () => apiClient.get<PlantLayout[]>('/plant-layouts/'),

  // Get plant layout details
  get: (id: number) => apiClient.get<PlantLayout>(`/plant-layouts/${id}/`),

  // Create new plant layout
  create: (data: {
    plant: number;
    name: string;
    background_image?: string;
    width?: number;
    height?: number;
    default_zoom?: number;
    default_center_x?: number;
    default_center_y?: number;
  }) => apiClient.post<PlantLayout>('/plant-layouts/', data),

  // Update plant layout
  update: (id: number, data: Partial<PlantLayout>) => 
    apiClient.patch<PlantLayout>(`/plant-layouts/${id}/`, data),

  // Delete plant layout
  delete: (id: number) => apiClient.delete(`/plant-layouts/${id}/`),

  // Get dashboard data
  getDashboard: (id: number) => 
    apiClient.get<PlantMappingDashboard>(`/plant-layouts/${id}/dashboard/`),

  // Get plant layout by plant ID
  getByPlant: (plantId: number) => 
    apiClient.get<PlantLayout>(`/plant-layouts/by-plant/${plantId}/`),

  // Get dashboard data by plant ID
  getDashboardByPlant: (plantId: number) => 
    apiClient.get<PlantMappingDashboard>(`/plant-layouts/by-plant/${plantId}/dashboard/`),
};

// Workshop Position API
export const workshopPositionApi = {
  // List workshop positions
  list: (params?: { layout?: number; positioned?: boolean }) => 
    apiClient.get<WorkshopPosition[]>('/workshop-positions/', { params }),

  // Get workshop position details
  get: (id: number) => apiClient.get<WorkshopPosition>(`/workshop-positions/${id}/`),

  // Update workshop position
  update: (id: number, data: {
    x?: number;
    y?: number;
    is_positioned?: boolean;
    zone?: string;
    layer?: number;
    size?: number;
    color?: string;
  }) => apiClient.patch<WorkshopPosition>(`/workshop-positions/${id}/`, data),

  // Bulk update positions
  bulkUpdate: (data: {
    positions: DragPosition[];
    change_reason?: string;
  }) => apiClient.post('/workshop-positions/bulk_update/', data),

  // Auto-position workshops
  autoPosition: (layoutId: number, data: {
    strategy: 'grid' | 'zone_based' | 'department_grouped' | 'status_grouped';
    zone_id?: number;
    spacing?: number;
    start_x?: number;
    start_y?: number;
    columns?: number;
  }) => apiClient.post(`/workshop-positions/auto_position/?layout=${layoutId}`, data),

  // Create missing position records
  createMissingPositions: (layoutId: number) => 
    apiClient.post(`/workshop-positions/create_missing_positions/?layout=${layoutId}`),
};

// Plant Zone API
export const plantZoneApi = {
  // List plant zones
  list: (params?: { layout?: number }) => 
    apiClient.get<PlantZone[]>('/plant-zones/', { params }),

  // Get plant zone details
  get: (id: number) => apiClient.get<PlantZone>(`/plant-zones/${id}/`),

  // Create new plant zone
  create: (data: {
    plant_layout: number;
    name: string;
    description?: string;
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    color?: string;
    opacity?: number;
    border_color?: string;
    border_style?: string;
    department?: number;
    capacity?: number;
  }) => apiClient.post<PlantZone>('/plant-zones/', data),

  // Update plant zone
  update: (id: number, data: Partial<PlantZone>) => 
    apiClient.patch<PlantZone>(`/plant-zones/${id}/`, data),

  // Delete plant zone
  delete: (id: number) => apiClient.delete(`/plant-zones/${id}/`),

  // Get workshops in zone
  getWorkshops: (id: number) => 
    apiClient.get(`/plant-zones/${id}/workshops/`),
};

// Position History API
export const positionHistoryApi = {
  // List position history
  list: (params?: { 
    workshop?: number; 
    user?: number; 
    start_date?: string; 
    end_date?: string; 
  }) => apiClient.get<PositionHistory[]>('/position-history/', { params }),

  // Get position history details
  get: (id: number) => apiClient.get<PositionHistory>(`/position-history/${id}/`),
};

// Plant Mapping Search API
export const plantMappingSearchApi = {
  // Search workshops
  searchWorkshops: (layoutId: number, params?: {
    query?: string;
    department?: number;
    status?: string;
    category?: string;
    positioned_only?: boolean;
    unpositioned_only?: boolean;
  }) => apiClient.get(`/plant-mapping-search/workshops/?layout=${layoutId}`, { params }),
};

// Utility functions
export const plantMappingUtils = {
  // Convert normalized coordinates to absolute
  normalizedToAbsolute: (x: number, y: number, layout: PlantLayout) => ({
    x: x * layout.width,
    y: y * layout.height
  }),

  // Convert absolute coordinates to normalized
  absoluteToNormalized: (x: number, y: number, layout: PlantLayout) => ({
    x: x / layout.width,
    y: y / layout.height
  }),

  // Get workshop status color
  getWorkshopStatusColor: (status: string) => {
    const colors = {
      'planned': '#6b7280',     // gray
      'ongoing': '#3b82f6',     // blue
      'completed': '#10b981',   // green
      'cancelled': '#ef4444',   // red
    };
    return colors[status as keyof typeof colors] || '#6b7280';
  },

  // Get SQDCM category color
  getSQDCMCategoryColor: (category: string) => {
    const colors = {
      'Safety': '#10b981',      // green
      'Quality': '#3b82f6',     // blue
      'Delivery': '#f59e0b',    // amber
      'Cost': '#fbbf24',        // yellow
      'Morale': '#ec4899',      // pink
    };
    return colors[category as keyof typeof colors] || '#6b7280';
  },

  // Check if point is within zone
  isPointInZone: (x: number, y: number, zone: PlantZone) => {
    return x >= zone.x1 && x <= zone.x2 && y >= zone.y1 && y <= zone.y2;
  },

  // Calculate distance between two points
  calculateDistance: (x1: number, y1: number, x2: number, y2: number) => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
  },

  // Find nearest zone to a point
  findNearestZone: (x: number, y: number, zones: PlantZone[]) => {
    let nearestZone: PlantZone | null = null;
    let minDistance = Infinity;

    zones.forEach(zone => {
      if (plantMappingUtils.isPointInZone(x, y, zone)) {
        return zone; // Point is inside zone
      }

      // Calculate distance to zone center
      const zoneCenterX = (zone.x1 + zone.x2) / 2;
      const zoneCenterY = (zone.y1 + zone.y2) / 2;
      const distance = plantMappingUtils.calculateDistance(x, y, zoneCenterX, zoneCenterY);

      if (distance < minDistance) {
        minDistance = distance;
        nearestZone = zone;
      }
    });

    return nearestZone;
  },

  // Generate grid positions
  generateGridPositions: (
    count: number, 
    startX: number, 
    startY: number, 
    spacing: number, 
    columns: number
  ) => {
    const positions: Array<{ x: number; y: number }> = [];
    
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / columns);
      const col = i % columns;
      
      positions.push({
        x: Math.min(startX + (col * spacing), 1.0),
        y: Math.min(startY + (row * spacing), 1.0)
      });
    }
    
    return positions;
  },

  // Format savings amount
  formatSavings: (amount: number, unit: string) => {
    if (!amount) return 'N/A';
    
    const formatted = amount.toLocaleString();
    return `${formatted} ${unit}`;
  },

  // Get workshop tooltip content
  getWorkshopTooltip: (workshop: WorkshopBasic) => {
    return {
      title: `Workshop #${workshop.workshop_number}`,
      subtitle: workshop.title,
      status: workshop.status,
      category: workshop.sqdcm_category,
      savings: plantMappingUtils.formatSavings(
        workshop.estimated_savings_per_year, 
        workshop.estimated_savings_unit
      )
    };
  },

  // Detect collisions between workshops
  detectCollisions: (positions: WorkshopPosition[], threshold: number = 0.03) => {
    const collisions: Array<{ workshop1: number; workshop2: number; distance: number }> = [];
    
    for (let i = 0; i < positions.length; i++) {
      for (let j = i + 1; j < positions.length; j++) {
        const pos1 = positions[i];
        const pos2 = positions[j];
        
        if (!pos1.is_positioned || !pos2.is_positioned) continue;
        
        const distance = plantMappingUtils.calculateDistance(pos1.x, pos1.y, pos2.x, pos2.y);
        
        if (distance < threshold) {
          collisions.push({
            workshop1: pos1.workshop.id,
            workshop2: pos2.workshop.id,
            distance
          });
        }
      }
    }
    
    return collisions;
  }
};
